import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Stack,
  Text,
  useTheme,
  Button,
  Tooltip,
  type SelectChangeEvent,
} from '@ltvco/refresh-lib/theme';
import { capitalizeEachWord, useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  AdvancedSearchURLParams,
  CollectionType,
  OptionType,
  ReportTypeSelect,
  getReportType,
  resolveReportType,
  trackTab,
  type REPORT_TYPES,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import {
  AutoAwesome,
  Cancel,
  Gavel,
  GridViewSharp,
  TableRowsSharp,
  FilterList,
  Edit,
} from '@mui/icons-material';
import { useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { NotificationMessage } from 'components/SearchpalChatbot/components/NotificationMessage';
import {
  NavButton,
  OptionResults,
  SearchHeader,
  SearchHeaderVariation,
} from 'routes/SearchPerson/components';
import { useSearchPersonResultsContext } from 'routes/SearchPerson/context';

import { CHATBOT_INTERACTION_TYPES, COURT_CASES } from '../constants';
import { viewTypes } from '../interfaces';
import { buildHeaderText } from '../utils';

import { ActiveFiltersButton, ViewTypeContainer } from './components';

export interface SearchHeaderProps {
  options: OptionType[];
  selectedOption: CollectionType | typeof COURT_CASES;
  // eslint-disable-next-line no-unused-vars
  setSelectedOption: (value: OptionType) => void;
  searchData: AdvancedSearchURLParams;
  isFilterCollapsed: boolean;
  // eslint-disable-next-line no-unused-vars
  setIsFilterCollapsed: (value: boolean) => void;
  isChatbotOpen: boolean;
  // eslint-disable-next-line no-unused-vars
  setIsChatbotOpen: (type: string) => void;
  initialNotificationVisible: boolean;
  // eslint-disable-next-line no-unused-vars
  setInitialNotificationVisible: (visible: boolean) => void;
  reportCounts?: Partial<Record<REPORT_TYPES, string>>;
}

export const SearchHeaderDesktop = ({
  options,
  selectedOption,
  setSelectedOption,
  searchData,
  isFilterCollapsed,
  setIsFilterCollapsed,
  isChatbotOpen,
  setIsChatbotOpen,
  initialNotificationVisible,
  setInitialNotificationVisible,
  reportCounts,
}: SearchHeaderProps) => {
  const selectedReportType = resolveReportType(searchData);
  const { trackEventGA4 } = useContext(AppConfig);
  const fullName = [searchData.fName, searchData.mName, searchData.lName]
    .filter(Boolean)
    .join(' ');
  const capitalizeFullName = capitalizeEachWord(
    fullName || searchData.fullName
  );
  const isInSearchResultsVariation = useFeatureIsOn('RFRSH-4236');
  const isInChatbotVariation = useFeatureIsOn('prop_2964');
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMobile, isDesktop } = useScreenSize();
  const [searchParams, setSearchParams] = useSearchParams();
  const isInChatbotVariationDesktop = isDesktop && isInChatbotVariation;
  const { currentView, setCurrentView, isOnMobileCardInDesktopVariation } =
    useSearchPersonResultsContext();

  const handleReportTypeChange = (event: SelectChangeEvent) => {
    const nextReportType =
      (event.target.value as REPORT_TYPES) || 'people_search';

    trackTab(trackEventGA4, nextReportType, 'search_results');

    if (nextReportType === 'court_cases') {
      const courtCasesUrl = `/dashboard/court-cases/cases?query=${encodeURIComponent(
        `"${searchParams.get('fname')} ${searchParams.get('lname')}"`
      )}`;
      navigate(courtCasesUrl);
      return;
    }

    const { selectedSources, type } = getReportType(nextReportType);
    const source =
      Object.keys(selectedSources).find(
        (reportType) => !!selectedSources[reportType]
      ) ?? '';

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('advancedSearch', 'true');
    nextParams.set('source', source);
    nextParams.set('type', type);
    setSearchParams(nextParams);
  };

  const getActiveFiltersCount = () => {
    let {
      age,
      state,
      city,
      address,
      title,
      company,
      industry,
      level,
      birthYear,
      birthMonth,
      deathYear,
      deathMonth,
      fullName,
      company_size_max,
      company_size_min,
    } = searchData;
    age = age !== '0' ? age : '';
    company_size_max = company_size_max !== '9' ? company_size_max : '';
    company_size_min = company_size_min !== '1' ? company_size_min : '';

    const filters = [
      age,
      state,
      city,
      address,
      title,
      company,
      industry,
      ...(level || []),
      birthYear,
      birthMonth,
      deathYear,
      deathMonth,
      fullName,
      company_size_max,
      company_size_min,
    ];
    return filters.filter(
      (val) => val && val.toString().trim() !== '' && val !== '[]'
    ).length;
  };

  const CollapsedFiltersButton = () => {
    if (!isFilterCollapsed || !isDesktop) {
      return null;
    }

    return (
      <ActiveFiltersButton
        startIcon={<FilterList sx={{ fontSize: 20 }} />}
        onClick={() => {
          trackEventGA4({
            event_name: 'search_results_interaction',
            type: 'open_filter',
          });
          setIsFilterCollapsed(false);
        }}
        sx={{ mr: 1 }}
        variant="outlined"
      >
        Filter results ({getActiveFiltersCount()})
      </ActiveFiltersButton>
    );
  };

  const viewOptions = [
    { type: viewTypes.GRID, icon: <GridViewSharp fontSize="small" /> },
    { type: viewTypes.TABLE, icon: <TableRowsSharp fontSize="small" /> },
  ];

  return isInSearchResultsVariation ? (
    <SearchHeaderVariation
      flexDirection={isMobile ? 'column' : 'row'}
      alignItems={isMobile ? 'start' : 'center'}
      justifyContent="space-between"
      gap={2}
      width="100%"
      display="flex"
      py={2}
    >
      <Stack
        minWidth={isMobile ? '100%' : '90%'}
        width={{ xs: '100%' }}
        flexDirection={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'start' : 'center'}
        gap={2}
      >
        <ReportTypeSelect
          value={selectedReportType}
          onChange={handleReportTypeChange}
          reportCounts={reportCounts}
          isOnHeader
        />
        <Text
          variant="h4"
          minWidth="80%"
          width={{ xs: '100%' }}
          sx={{
            mb: 0,
            textWrap: 'nowrap',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {buildHeaderText(capitalizeFullName, searchData)}
        </Text>
      </Stack>

      <Button
        variant="text"
        startIcon={
          <Edit sx={{ fontSize: 20, color: theme.palette.primary.main }} />
        }
        onClick={() => {
          trackEventGA4({
            event_name: 'search_results_interaction',
            type: 'open_filter',
          });
          setIsFilterCollapsed(false);
        }}
        sx={{ whiteSpace: 'nowrap' }}
      >
        Edit Search
      </Button>
    </SearchHeaderVariation>
  ) : (
    <SearchHeader
      flexDirection="column"
      alignItems="left"
      pt={2.5}
      pb={2.5}
      display={'flex'}
    >
      <Stack flexDirection="row" order={2}>
        <Stack flexDirection="row" flexWrap="wrap" ml={1.25} gap={1}>
          <CollapsedFiltersButton />
          {options
            .filter((option) => option.visible)
            .map((option) => {
              const button = (
                <NavButton
                  onClick={() => setSelectedOption(option)}
                  className={selectedOption === option.value ? 'active' : ''}
                >
                  <Stack flexDirection="row" mt={0} alignItems="center">
                    <OptionResults
                      className="options-results"
                      sx={{
                        width: parseInt(option.length) > 9 ? '40px' : '25px',
                        marginRight: '5px',
                      }}
                    >
                      {option.value === COURT_CASES ? (
                        <Gavel sx={{ fontSize: '16px' }} />
                      ) : option.length ? (
                        option.length
                      ) : (
                        '0'
                      )}
                    </OptionResults>
                    <Text variant="body1" m={0}>
                      {option.label}
                    </Text>
                  </Stack>
                </NavButton>
              );

              return (
                <Tooltip
                  key={option.value}
                  title={option.tooltipContent}
                  arrow
                  disableInteractive
                  sx={(theme) => ({
                    '.MuiTooltip-arrow': {
                      color: 'common.white',
                      '&::before': {
                        boxShadow: `0px 0px 1px ${theme.palette.text.disabled}`,
                      },
                    },
                  })}
                >
                  {button}
                </Tooltip>
              );
            })}
        </Stack>
      </Stack>

      <Stack
        className="results-for"
        flexDirection="row"
        ml={1.25}
        sx={{
          order: 1,
          mb: 2,
          mt: 1,
        }}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" alignItems="center">
          <Text
            variant="h3"
            sx={{
              fontWeight: 'normal',
              marginRight: '5px',
              marginBottom: '0',
            }}
          >
            Results for:
          </Text>
          <Text variant="h3" m={0}>
            {capitalizeFullName}
          </Text>
        </Stack>
        {isOnMobileCardInDesktopVariation && (
          <Stack flexDirection="row" ml="auto">
            {viewOptions.map((option) => (
              <ViewTypeContainer
                key={option.type}
                className={currentView === option.type ? 'active' : ''}
                onClick={() => setCurrentView && setCurrentView(option.type)}
              >
                {option.icon}
              </ViewTypeContainer>
            ))}
          </Stack>
        )}
        {isInChatbotVariationDesktop && (
          <>
            <Button
              variant={'outlined'}
              size="small"
              onClick={() => {
                if (isChatbotOpen) {
                  setIsChatbotOpen(CHATBOT_INTERACTION_TYPES.CLOSE);
                } else {
                  setIsChatbotOpen(CHATBOT_INTERACTION_TYPES.OPEN);
                  setInitialNotificationVisible(false);
                }
              }}
              sx={{
                px: 1,
                py: 0.5,
                gap: 0.5,
              }}
            >
              {isChatbotOpen ? (
                <Cancel sx={{ color: theme.palette.primary.main }} />
              ) : (
                <AutoAwesome sx={{ color: theme.palette.primary.main }} />
              )}{' '}
              <Text color={theme.palette.premium.contrastText} variant="body2">
                {' '}
                {isChatbotOpen ? 'Close' : 'Search Assistant'}
              </Text>
            </Button>
            <NotificationMessage
              initialNotificationVisible={initialNotificationVisible}
              setInitialNotificationVisible={setInitialNotificationVisible}
              capitalizeFullName={capitalizeFullName}
              setIsChatbotOpen={setIsChatbotOpen}
            />
          </>
        )}
      </Stack>
    </SearchHeader>
  );
};
